// Artwork coverage checker — verifies artwork's non-transparent pixels
// fully cover the product area on a template.
// L1 rect check runs on main thread (fast). L2 pixel-perfect check
// delegated to OpenCV Web Worker (cv-coverage.worker.js).

import { checkCoverageWithWorker } from '../webworkers/helpers/coverage-checker'

/**
 * Detect if image has any transparent pixels.
 * Draws a 50×50 thumbnail and checks alpha channel.
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @returns {boolean}
 */
export const hasTransparency = (image) => {
    const canvas = document.createElement('canvas')
    const size = 50
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.drawImage(image, 0, 0, size, size)
    const data = ctx.getImageData(0, 0, size, size).data

    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 128) return true
    }
    return false
}

/**
 * L1 — Rect check for opaque images. Compares artwork bounding box vs safezone.
 * @param {{ x: number, y: number, width: number, height: number }} safezone
 * @param {{ x: number, y: number, width: number, height: number }} placement
 * @returns {{ ok: boolean, coveragePercent: number, uncoveredEdges: string[], uncoveredCorners: string[] }}
 */
export const checkCoverageRect = (safezone, placement) => {
    const sz = {
        left: safezone.x,
        top: safezone.y,
        right: safezone.x + safezone.width,
        bottom: safezone.y + safezone.height,
    }
    const aw = {
        left: placement.x,
        top: placement.y,
        right: placement.x + placement.width,
        bottom: placement.y + placement.height,
    }

    const uncoveredEdges = []
    if (aw.left > sz.left) uncoveredEdges.push('left')
    if (aw.top > sz.top) uncoveredEdges.push('top')
    if (aw.right < sz.right) uncoveredEdges.push('right')
    if (aw.bottom < sz.bottom) uncoveredEdges.push('bottom')

    // Calculate overlap percentage
    const overlapW = Math.max(0, Math.min(aw.right, sz.right) - Math.max(aw.left, sz.left))
    const overlapH = Math.max(0, Math.min(aw.bottom, sz.bottom) - Math.max(aw.top, sz.top))
    const szArea = safezone.width * safezone.height
    const coveragePercent = szArea > 0
        ? Math.round((overlapW * overlapH) / szArea * 10000) / 100
        : 0

    // Derive corners from edges
    const uncoveredCorners = []
    if (uncoveredEdges.includes('top') && uncoveredEdges.includes('left')) uncoveredCorners.push('top-left')
    if (uncoveredEdges.includes('top') && uncoveredEdges.includes('right')) uncoveredCorners.push('top-right')
    if (uncoveredEdges.includes('bottom') && uncoveredEdges.includes('left')) uncoveredCorners.push('bottom-left')
    if (uncoveredEdges.includes('bottom') && uncoveredEdges.includes('right')) uncoveredCorners.push('bottom-right')

    return {
        ok: uncoveredEdges.length === 0,
        coveragePercent,
        uncoveredEdges,
        uncoveredCorners,
    }
}

/**
 * Main entry — routes to L1 (rect) or L2 (OpenCV worker) based on image transparency.
 * Async — L2 runs in Web Worker for pixel-perfect coverage check.
 * @param {{ x: number, y: number, width: number, height: number }} safezone
 * @param {{ x: number, y: number, width: number, height: number, rotation?: number }} placement
 * @param {HTMLImageElement|HTMLCanvasElement} image - artwork image
 * @param {{ width: number, height: number }} templateSize
 * @param {string|null} [templateSrc] - template image URL for worker fetch
 * @returns {Promise<{ ok: boolean, coveragePercent: number, uncoveredEdges: string[], uncoveredCorners: string[] }>}
 */
export const checkArtworkCoverage = async (safezone, placement, image, templateSize, templateSrc = null, artOffscreenCanvas = null) => {
    if (!safezone) {
        return { ok: true, coveragePercent: 100, uncoveredEdges: [], uncoveredCorners: [] }
    }

    // L1 rect check — if bounding box doesn't cover, fail fast
    const rectResult = checkCoverageRect(safezone, placement)
    if (!rectResult.ok) {
        console.log('[Coverage] L1 FAIL', rectResult)
        return rectResult
    }

    // L1 passed. For transparent PNGs, delegate to OpenCV worker for pixel-perfect check
    const transparent = hasTransparency(image)
    console.log('[Coverage] L1 PASS, hasTransparency:', transparent, 'templateSrc:', !!templateSrc)
    if (transparent && templateSrc) {
        console.log('[Coverage] → delegating to OpenCV worker')
        // Get ImageBitmap from pre-drawn OffscreenCanvas (created once at img.onload).
        // transferToImageBitmap() is synchronous ~0ms — no GPU upload, no decode, no network.
        // NOTE: transferToImageBitmap() clears the canvas backing store → must redraw for next call.
        // Falls back to createImageBitmap(image) if OffscreenCanvas not available.
        const t0 = performance.now()
        let artBitmap
        if (artOffscreenCanvas) {
            artBitmap = artOffscreenCanvas.transferToImageBitmap()
            console.log(`[Coverage] transferToImageBitmap: ${(performance.now()-t0).toFixed(1)}ms`)
            // Redraw AFTER postMessage (async) so it doesn't block the main thread before IPC.
            // drawImage(image) onto OffscreenCanvas takes ~20-80ms for large images.
            Promise.resolve().then(() => {
                artOffscreenCanvas.getContext('2d').drawImage(image, 0, 0)
            })
        } else {
            // Fallback: OffscreenCanvas not pre-drawn (e.g. not supported)
            artBitmap = await createImageBitmap(image)
            console.log(`[Coverage] createImageBitmap fallback: ${(performance.now()-t0).toFixed(1)}ms`)
        }
        const result = await checkCoverageWithWorker({
            artBitmap,              // transfer decoded pixels directly — worker needs no fetch
            templateSrc,
            placement,
            templateSize,
            safezoneRect: safezone,
        })
        console.log(`[Coverage] worker Promise resolved @ ${new Date().toISOString().slice(11,23)}`)
        // null means this call was superseded by a newer one — caller should ignore
        if (result === null) return null
        return result
    }

    // Opaque image + bounding box covers → fully covered
    return rectResult
}
